/**
 * Shared Upload Utilities
 *
 * Common formatting, URL processing, and type checking functions
 * used across the crosshair gallery and admin uploads page.
 */
/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes) {
    if (!bytes || isNaN(bytes))
        return 'Unknown size';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/**
 * Format date/time in UK format
 */
export function formatUploadDate(value, options) {
    if (!value)
        return 'Unknown';
    try {
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime()))
            return 'Unknown';
        const { includeTime = true } = options || {};
        if (includeTime) {
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        }
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    }
    catch {
        return 'Unknown';
    }
}
/**
 * Check if a file is an image based on MIME type or extension
 */
export function isImageFile(upload) {
    if (upload.mimetype && upload.mimetype.startsWith('image/')) {
        return true;
    }
    const filename = upload.filename.toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|heic)$/.test(filename);
}
/**
 * Process image URL to use resize endpoint for thumbnails
 */
export function processImageUrl(url, size = 'small') {
    if (!url)
        return url;
    // Replace domain with current origin
    let processedUrl = url.replace(/https?:\/\/maps\.cfn\.ltd/g, window.location.origin);
    // Replace /uploads/ with /upload/resize/{size}/ for thumbnails
    processedUrl = processedUrl.replace(/\/uploads\//g, `/upload/resize/${size}/`);
    return processedUrl;
}
/**
 * Get full-size URL (normalise domain only)
 */
export function getFullSizeUrl(url) {
    if (!url)
        return url;
    return url.replace(/https?:\/\/maps\.cfn\.ltd/g, window.location.origin);
}
/**
 * Get download URL for an upload
 */
export function getDownloadUrl(url) {
    const fullUrl = getFullSizeUrl(url);
    // Convert /uploads/ path to /upload/ for download endpoint
    return fullUrl.replace(/\/uploads\//, '/upload/');
}
export function formatDualDates(fileDate, uploadTime) {
    const formattedFileDate = fileDate ? formatUploadDate(fileDate) : null;
    const formattedUploadTime = uploadTime ? formatUploadDate(uploadTime) : null;
    if (formattedFileDate && formattedUploadTime) {
        return {
            primary: formattedFileDate,
            secondary: formattedUploadTime,
            hasBothDates: true,
        };
    }
    // Fallback to whichever is available
    const available = formattedFileDate || formattedUploadTime || 'Unknown';
    return {
        primary: available,
        secondary: '',
        hasBothDates: false,
    };
}
